/**
 * Client logger + App Insights initialization. Import FIRST in client/main.tsx (before <App/>), so the
 * Skypoint logger's global browser error handler and the App Insights init run before render.
 *
 * Routes UI telemetry -> resource `appins-skycare-ui` via the build-time connection string
 * `VITE_APPINSIGHTS_UI_CONNECTION_STRING` (Vite inlines VITE_* into the bundle; the connection string
 * is an ingestion endpoint, NOT the read-only query key — the *_API_KEY must NEVER appear in client
 * code). Telemetry flows through the @skypointcloud/logger conventions (same correlationId/appName as
 * the backend) so the two App Insights resources join on a shared correlationId.
 *
 * HIPAA: the browser App Insights SDK auto-tracks fetch/XHR as `dependencies` with the full target URL
 * incl. query string and auto-route-tracking records the URL. The vendored logger@1.2.0 enables those by
 * default and exposes no knob to disable them, so we register an `addTelemetryInitializer` that runs the
 * same query-string/PHI scrub as the backend. We attach it via the PUBLIC web SDK instance hook
 * (`ApplicationInsights.addTelemetryInitializer`), falling back to the ReactPlugin core only with a loud
 * console.error; if NO hook can be found we console.error a HIPAA warning AND disable auto dependency/
 * route collection rather than shipping raw URLs (H3/B-H1). Primary defense remains the house rule:
 * member identity travels in the URL PATH as an opaque key, never in a query string.
 *
 * NOTE (vendored constraint): the vendored transport builds the web SDK inside an async `initialize()`,
 * so the SDK instance + its core do not exist until `result.ready` resolves — we therefore attach as
 * early as that promise allows rather than truly synchronously at config time (which would require
 * forking the vendored dep). The window before attach is covered by the house path-only rule.
 */
import { initLogger, LogLevel, logger } from "@skypointcloud/logger/react";
import type { ReactPlugin } from "@skypointcloud/logger/react";
import { sanitizeTelemetryItem, sanitizeUrl } from "./lib/phi-redact";

const connectionString = import.meta.env.VITE_APPINSIGHTS_UI_CONNECTION_STRING as string | undefined;

const result = initLogger({
  appName: "skycare-client",
  environment: import.meta.env.MODE,
  appInsightsConnectionString: connectionString,
  minLogLevel: import.meta.env.PROD ? LogLevel.INFO : LogLevel.DEBUG,
});

export const reactPlugin = result.reactPlugin as ReactPlugin | undefined;
export const appInsights = result.appInsights; // for explicit, sanitized trackPageView/trackEvent

/** The subset of the App Insights web SDK / core surface we touch for PHI gating. */
interface TelemetryInitializerHost {
  addTelemetryInitializer?: (fn: (item: unknown) => boolean | void) => unknown;
  config?: {
    disableAjaxTracking?: boolean;
    disableFetchTracking?: boolean;
    enableAutoRouteTracking?: boolean;
  };
}

/** The vendored transport keeps the raw web SDK instance on a private `client` field. */
function rawSdk(): TelemetryInitializerHost | undefined {
  return (appInsights as unknown as { client?: TelemetryInitializerHost } | undefined)?.client;
}

/**
 * Register the PHI/query-string scrubber. Prefer the PUBLIC web SDK instance hook; fall back to the
 * ReactPlugin core (public IAppInsightsCore method, but reaching it via the plugin is less stable) with
 * a loud error. Returns which hook attached, or "none" when neither is available.
 */
function attachSanitizer(): "public" | "plugin-core" | "none" {
  const sdk = rawSdk();
  if (sdk && typeof sdk.addTelemetryInitializer === "function") {
    sdk.addTelemetryInitializer((item) => sanitizeTelemetryItem(item));
    return "public";
  }
  const core = (reactPlugin as unknown as { core?: TelemetryInitializerHost } | undefined)?.core;
  if (core && typeof core.addTelemetryInitializer === "function") {
    core.addTelemetryInitializer((item) => sanitizeTelemetryItem(item));
    // eslint-disable-next-line no-console
    console.error(
      "[App Insights] PHI scrubber attached via reactPlugin.core fallback (public SDK hook unavailable) - verify SDK wiring.",
    );
    return "plugin-core";
  }
  return "none";
}

/**
 * Last-resort HIPAA guard: if no telemetry initializer hook is reachable, turn OFF the SDK's auto
 * dependency/route collection so it cannot ship full (potentially PHI-bearing) URLs unscrubbed. The web
 * SDK v3 config is a dynamic proxy, so these take effect at runtime.
 */
function disableAutoCollection(): void {
  try {
    const cfg = rawSdk()?.config;
    if (cfg) {
      cfg.disableAjaxTracking = true;
      cfg.disableFetchTracking = true;
      cfg.enableAutoRouteTracking = false;
    }
  } catch (err) {
    // eslint-disable-next-line no-console
    console.error("[App Insights] Failed to disable auto-collection after missing PHI hook:", err);
  }
}

/**
 * Emit a query-free initial page view once the session bootstrap (GET /api/v1/me) has adopted the
 * server-echoed correlation id, so the page view carries the SAME correlationId the backend recorded
 * (the two App Insights resources then join on the shared dimension). Path only — query stripped.
 */
function scheduleInitialPageView(): void {
  setTimeout(() => {
    try {
      const path = typeof window !== "undefined" ? sanitizeUrl(window.location.pathname) || "/" : "/";
      const correlationId = logger.getCorrelationId();
      appInsights?.trackPageView(path, {
        appName: "skycare-client",
        ...(correlationId ? { correlationId } : {}),
      });
    } catch (err) {
      // eslint-disable-next-line no-console
      console.debug("[App Insights] initial trackPageView skipped:", err);
    }
  }, 2500);
}

// Wire the PHI gate as soon as the SDK is ready. Guard on `appInsights` so local dev (no connection
// string -> no SDK) does nothing rather than tripping the missing-hook path.
result.ready
  .then(() => {
    if (!appInsights) return; // telemetry not configured (local dev) — nothing to scrub
    const mode = attachSanitizer();
    if (mode === "none") {
      // eslint-disable-next-line no-console
      console.error(
        "[App Insights] HIPAA WARNING: no telemetry initializer hook found - auto AJAX/route tracking " +
          "could ship UNREDACTED URLs. Disabling auto dependency/route collection.",
      );
      disableAutoCollection();
    }
    scheduleInitialPageView();
  })
  .catch((err) => {
    // App Insights failed to initialize — console logging still works. Surface (don't swallow) the cause.
    // eslint-disable-next-line no-console
    console.debug("[App Insights] not initialized; console logging only:", err);
  });

/**
 * Emit a sanitized page view for a SPA route change. Call from route effects to keep UI telemetry
 * meaningful without auto-route tracking (which would capture query strings). Path only — no query.
 */
export function trackRoute(pathname: string): void {
  try {
    const correlationId = logger.getCorrelationId();
    appInsights?.trackPageView(sanitizeUrl(pathname) || "/", {
      appName: "skycare-client",
      ...(correlationId ? { correlationId } : {}),
    });
  } catch (err) {
    // eslint-disable-next-line no-console
    console.debug("[App Insights] trackRoute skipped:", err);
  }
}
